import React from 'react';
import SelectBase, {
    Props as SelectProps,
    components,
    StylesConfig,
    CSSObjectWithLabel,
    GroupBase,
    DropdownIndicatorProps,
    MenuProps,
    MenuListProps,
    OptionProps,
} from 'react-select';
import {
    DropdownSelectWrapper,
    DropdownIndicatorWrapper,
    DropdownIndicatorArrow,
    MenuOuterWrapper,
    MenuListWrapper,
    OptionWrapper,
    getDefaultStyles,
} from './DropdownWrapper.style';

export type StyleFunctionType = (base: CSSObjectWithLabel, state: unknown) => CSSObjectWithLabel;

const identityStyleFunction: StyleFunctionType = (base) => base;

const createMergedStyleFunction = (
    defaultStyle: StyleFunctionType | undefined,
    customStyle: StyleFunctionType | undefined
): StyleFunctionType => {
    if (defaultStyle !== undefined && customStyle !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (base: CSSObjectWithLabel, state: any): CSSObjectWithLabel => {
            const defaultResult = defaultStyle(base, state);
            const customResult = customStyle(defaultResult, state);
            return customResult;
        };
    }

    if (customStyle !== undefined) {
        return customStyle;
    }

    if (defaultStyle !== undefined) {
        return defaultStyle;
    }

    return identityStyleFunction;
};

const mergeStyles = <Option, IsMulti extends boolean>(
    customStyles: StylesConfig<Option, IsMulti> | undefined,
    hasInternalLabel: boolean
): StylesConfig<Option, IsMulti> => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const defaultStyles = getDefaultStyles(hasInternalLabel) as StylesConfig<Option, IsMulti>;

    if (customStyles === undefined) {
        return defaultStyles;
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const merged = {} as StylesConfig<Option, IsMulti>;
    const allKeys = new Set([...Object.keys(defaultStyles), ...Object.keys(customStyles)]);

    allKeys.forEach((key) => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const styleKey = key as keyof StylesConfig<Option, IsMulti>;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const defaultStyle = defaultStyles[styleKey] as StyleFunctionType | undefined;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const customStyle = customStyles[styleKey] as StyleFunctionType | undefined;

        const mergedStyle = createMergedStyleFunction(defaultStyle, customStyle);
        // react-select's StylesConfig has incompatible function signatures for different style keys
        // We need to use 'any' here because TypeScript cannot reconcile the union types
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
        merged[styleKey] = mergedStyle as any;
    });

    return merged;
};

interface DropdownSelectPropsType<Option = unknown, IsMulti extends boolean = false>
    extends Omit<SelectProps<Option, IsMulti>, 'styles'> {
    customStyles?: StylesConfig<Option, IsMulti>;
    hasInternalLabel?: boolean;
}

function DropdownSelect<Option = unknown, IsMulti extends boolean = false>(
    props: DropdownSelectPropsType<Option, IsMulti>
): JSX.Element {
    const {
        customStyles,
        hasInternalLabel = false,
        className = '',
        classNamePrefix = 'Select',
        placeholder = '',
        isClearable = false,
        isSearchable = false,
        backspaceRemovesValue = false,
        components: customComponents = {},
        ...restProps
    } = props;

    const finalStyles = React.useMemo(
        () => mergeStyles(customStyles, hasInternalLabel),
        [customStyles, hasInternalLabel]
    );

    const mergedComponents = React.useMemo(
        () => ({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
            Option: CommonOption as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
            Menu: CommonMenu as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
            MenuList: CommonMenuList as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
            DropdownIndicator: CommonDropdownIndicator as any,
            ...customComponents,
        }),
        [customComponents]
    );

    return (
        <DropdownSelectWrapper>
            <SelectBase<Option, IsMulti>
                className={className}
                classNamePrefix={classNamePrefix}
                placeholder={placeholder}
                isClearable={isClearable}
                isSearchable={isSearchable}
                backspaceRemovesValue={backspaceRemovesValue}
                styles={finalStyles}
                components={mergedComponents}
                menuShouldScrollIntoView={false}
                {...restProps}
            />
        </DropdownSelectWrapper>
    );
}

type CommonDropdownIndicatorPropsType = DropdownIndicatorProps<unknown, boolean, GroupBase<unknown>>;
type CommonMenuPropsType = MenuProps<unknown, boolean, GroupBase<unknown>>;
type CommonMenuListPropsType = MenuListProps<unknown, boolean, GroupBase<unknown>>;
type CommonOptionPropsType = OptionProps<unknown, boolean, GroupBase<unknown>>;

const CommonDropdownIndicator = (props: CommonDropdownIndicatorPropsType): JSX.Element => (
    <components.DropdownIndicator {...props}>
        <DropdownIndicatorWrapper>
            <DropdownIndicatorArrow position='right' />
            <DropdownIndicatorArrow position='left' />
        </DropdownIndicatorWrapper>
    </components.DropdownIndicator>
);

const CommonMenu = (props: CommonMenuPropsType): JSX.Element => (
    <MenuOuterWrapper>
        <components.Menu {...props}>{props.children}</components.Menu>
    </MenuOuterWrapper>
);

const CommonMenuList = (props: CommonMenuListPropsType): JSX.Element => (
    <MenuListWrapper>
        <components.MenuList {...props}>{props.children}</components.MenuList>
    </MenuListWrapper>
);

const CommonOption = (props: CommonOptionPropsType): JSX.Element => {
    const { data, innerRef, innerProps, isFocused, isSelected, children } = props;

    const dataLabel =
        data !== null && data !== undefined && typeof data === 'object' && 'label' in data ? String(data.label) : '';

    return (
        <OptionWrapper
            ref={innerRef}
            {...innerProps}
            isSelected={isSelected}
            isFocused={isFocused}
            role='option'
            aria-selected={isSelected}
        >
            {children !== null && children !== undefined && children !== '' ? children : <span>{dataLabel}</span>}
        </OptionWrapper>
    );
};

export type * from 'react-select';
export { components };
export { DropdownSelect };
export { createMergedStyleFunction, mergeStyles };
